import React, { useEffect, useRef } from "react";
import { paintStrokes } from "./strokes";

/**
 * Square canvas that paints a stroke store. The caller's className sizes the
 * wrapper; the canvas takes the largest centered square inside it.
 * Read-only on the board; on the drawer's phone the pointer handlers report
 * normalized points through onStrokeStart / onStrokeMove / onStrokeEnd.
 */
const DrawCanvas = ({
  store,
  readOnly = false,
  onStrokeStart,
  onStrokeMove,
  onStrokeEnd,
  className = "",
}) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const activePointer = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let frame = 0;

    const paint = () => {
      frame = 0;
      paintStrokes(ctx, store.get(), canvas.width);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(paint);
    };
    const resize = () => {
      // Largest square that fits the wrapper, whatever shape the wrapper is.
      const side = Math.floor(Math.min(wrap.clientWidth, wrap.clientHeight));
      canvas.style.width = `${side}px`;
      canvas.style.height = `${side}px`;
      const px = Math.round(side * Math.min(window.devicePixelRatio || 1, 2));
      if (px && px !== canvas.width) {
        canvas.width = px;
        canvas.height = px;
      }
      schedule();
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(wrap);
    const unsubscribe = store.subscribe(schedule);
    return () => {
      observer.disconnect();
      unsubscribe();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [store]);

  const toPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clamp = (n) => Math.min(1, Math.max(0, n));
    return [
      clamp((e.clientX - rect.left) / rect.width),
      clamp((e.clientY - rect.top) / rect.height),
    ];
  };

  const handleDown = (e) => {
    if (activePointer.current !== null) return; // one finger draws
    activePointer.current = e.pointerId;
    // Capture keeps the stroke alive when the finger leaves the canvas; it is
    // a nicety, so a browser refusing it must not stop the stroke.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
    onStrokeStart?.(toPoint(e));
  };

  const handleMove = (e) => {
    if (activePointer.current !== e.pointerId) return;
    const events = e.nativeEvent.getCoalescedEvents?.() || [];
    onStrokeMove?.((events.length ? events : [e]).map(toPoint));
  };

  const handleUp = (e) => {
    if (activePointer.current !== e.pointerId) return;
    activePointer.current = null;
    onStrokeEnd?.();
  };

  return (
    <div
      ref={wrapRef}
      className={`flex items-center justify-center ${className}`}
    >
      <canvas
        ref={canvasRef}
        className="block rounded-lg"
        style={readOnly ? undefined : { touchAction: "none" }}
        {...(!readOnly && {
          onPointerDown: handleDown,
          onPointerMove: handleMove,
          onPointerUp: handleUp,
          onPointerCancel: handleUp,
        })}
      />
    </div>
  );
};

export default DrawCanvas;
